import React, { useState } from "react";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import { itemSchema } from "../../schemas/item";
import useItemService from "../../services/api/itemService";

import { DownloadIcon } from "../../assets/icons";
import { Button, Loading, PageTitle, Section } from "../../components";
import {
  EyeMeasurementInput,
  PriceInput,
  SelectInput,
  TextInput,
} from "../../components/forms";
import "./InventoryPage.scss";

function AddItemPage() {
  const [isPending, setIsPending] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { createItem, queryKey } = useItemService();

  const mutation = useMutation({
    mutationFn: createItem,
    onSuccess: async (payload) => {
      await queryClient.resetQueries({
        queryKey: [queryKey],
      });
      toast.success(payload?.message);
      navigate("/inventory");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
    onSettled: () => {
      setIsPending(false);
    },
  });

  const defaultAttributesWrapper = {
    [enums.ITEM_CATEGORIES.LENSE]: {
      lense_type: "",
      power_range: {
        sph: null,
        cyl: null,
        add: 0,
      },
      coating: "",
      thickness: "",
      manufacture_country: "",
      transition: null,
      seg: null,
      index: "",
      free_form_design: "",
      tint: "",
    },
    [enums.ITEM_CATEGORIES.FRAME]: {
      model_number: "",
      material: "",
      color: "",
      size: {
        lens_diagonal: "",
        bridge: "",
        arm_length: "",
      },
    },
    [enums.ITEM_CATEGORIES.SUNGLASS]: {
      shape: "",
      frame_material: "",
      frame_color: "",
      lens_type: "",
      gender: "",
    },
    [enums.ITEM_CATEGORIES.ACCESSORIES]: {
      type: "",
      material: "",
      color: "",
    },
  };

  const initialValues = {
    brand: "",
    itemName: "",
    category: enums.ITEM_CATEGORIES.FRAME,
    purchasePrice: "",
    sellingPrice: "",
    warrantyPeriodMonths: 0,
    stock: {
      stockStatus: enums.STOCK_STATUS.READY,
      storeRoomStock: 0,
      displayStock: 0,
    },
    attributes: defaultAttributesWrapper[enums.ITEM_CATEGORIES.FRAME],
  };

  function handleOnSubmit(values) {
    setIsPending(true);
    mutation.mutate({ item: values });
  }

  return (
    <div className="page add-item-page">
      <PageTitle text="Add New Item" prev="/inventory" />

      <Section>
        <Formik
          initialValues={initialValues}
          validationSchema={itemSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="general-info">
                <div className="form-section-title">
                  <span>01</span>
                  <h1>General Information</h1>
                </div>

                <div className="input-group">
                  <TextInput
                    name="brand"
                    label="Brand"
                    placeholder="Enter brand"
                  />
                  <TextInput
                    name="itemName"
                    label="Item Name"
                    placeholder="Type item name here"
                  />
                </div>

                <div className="input-group">
                  <SelectInput
                    name="category"
                    label="Category"
                    onChange={(val) => {
                      setFieldValue("category", val);
                      setFieldValue(
                        "attributes",
                        defaultAttributesWrapper[val],
                      );
                    }}
                    values={[
                      { value: enums.ITEM_CATEGORIES.FRAME, label: "FRAME" },
                      { value: enums.ITEM_CATEGORIES.LENSE, label: "LENSE" },
                      {
                        value: enums.ITEM_CATEGORIES.ACCESSORIES,
                        label: "ACCESSORY",
                      },
                      {
                        value: enums.ITEM_CATEGORIES.SUNGLASS,
                        label: "SUNGLASS",
                      },
                    ]}
                  />

                  <SelectInput
                    name="stock.stockStatus"
                    label="Stock Status"
                    values={[
                      { value: enums.STOCK_STATUS.READY, label: "READY" },
                      {
                        value: enums.STOCK_STATUS.PRE_ORDER,
                        label: "PRE-ORDER",
                      },
                    ]}
                  />
                </div>

                {values.stock.stockStatus === enums.STOCK_STATUS.READY ? (
                  <div className="input-group">
                    <TextInput
                      name="stock.storeRoomStock"
                      label="Storeroom Stock"
                      placeholder="Enter quantity"
                      type="number"
                    />
                    <TextInput
                      name="stock.displayStock"
                      label="Showroom Stock"
                      placeholder="Enter quantity"
                      type="number"
                    />
                  </div>
                ) : null}
              </div>

              <div className="other-details">
                <div className="form-section-title">
                  <span>02</span>
                  <h1>Category Details</h1>
                </div>

                {/* LENSE FIELDS */}
                {values.category === enums.ITEM_CATEGORIES.LENSE ? (
                  <>
                    <div className="input-group">
                      <SelectInput
                        name="attributes.lense_type"
                        label="Lense Type"
                        values={[
                          { label: "Select Lense Type", value: "" },
                          ...Object.values(enums.LENSE_TYPES).map((t) => ({
                            label: t,
                            value: t,
                          })),
                        ]}
                      />
                    </div>
                    <div className="input-group">
                      <div className="input">
                        <label>SPH</label>
                        <EyeMeasurementInput name="attributes.power_range.sph" />
                      </div>
                      <div className="input">
                        <label>CYL</label>
                        <EyeMeasurementInput name="attributes.power_range.cyl" />
                      </div>
                      <div className="input">
                        <TextInput
                          type="number"
                          name="attributes.power_range.add"
                          label="ADD"
                          placeholder="Enter value"
                          step="0.25"
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <SelectInput
                        name="attributes.coating"
                        label="Coating"
                        onChange={(val) => {
                          setFieldValue("attributes.coating", val);
                          if (val !== enums.LENSE_COATINGS.PHOTO_GREY) {
                            setFieldValue("attributes.transition", null);
                          }
                        }}
                        values={[
                          { label: "Select Coating", value: "" },
                          ...Object.values(enums.LENSE_COATINGS).map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                      />
                      {values.attributes.coating ===
                      enums.LENSE_COATINGS.PHOTO_GREY ? (
                        <TextInput
                          name="attributes.transition"
                          label="Transition Level"
                          placeholder="Enter level (1-4)"
                          type="number"
                        />
                      ) : null}
                    </div>
                    <div className="input-group">
                      <TextInput
                        name="attributes.thickness"
                        label="Thickness"
                        placeholder="Enter thickness"
                        type="number"
                        step="0.01"
                      />
                      <TextInput
                        name="attributes.manufacture_country"
                        label="Manufacture Country"
                        placeholder="Enter country"
                      />
                      <TextInput
                        name="attributes.seg"
                        label="SEG"
                        placeholder="Enter seg"
                      />
                    </div>
                    <div className="input-group">
                      <TextInput
                        name="attributes.index"
                        label="Index"
                        placeholder="Enter index"
                        type="number"
                        step="0.01"
                        isDataListed
                        dataListItems={["1.49", "1.56", "1.61", "1.67", "1.74"]}
                      />
                      <SelectInput
                        name="attributes.free_form_design"
                        label="Free Form Design"
                        values={[
                          { label: "Select Design", value: "" },
                          ...Object.values(enums.FREE_FORM_DESIGNS).map(
                            (d) => ({
                              label: d,
                              value: d,
                            }),
                          ),
                        ]}
                      />
                      <TextInput
                        name="attributes.tint"
                        label="Tint"
                        placeholder="e.g. 30%, 50%, FULL, or custom"
                        isDataListed
                        dataListItems={["30%", "50%", "75%", "FULL"]}
                      />
                    </div>
                  </>
                ) : null}

                {/* FRAME FIELDS */}
                {values.category === enums.ITEM_CATEGORIES.FRAME ? (
                  <>
                    <div className="input-group">
                      <TextInput
                        name="attributes.model_number"
                        label="Model Number"
                        placeholder="Enter model number"
                      />
                      <TextInput
                        name="attributes.material"
                        label="Material"
                        placeholder="Enter material"
                      />
                      <TextInput
                        name="attributes.color"
                        label="Color"
                        placeholder="Enter color"
                      />
                    </div>
                    <div className="input-group">
                      <TextInput
                        name="attributes.size.lens_diagonal"
                        label="Lens Diagonal"
                        placeholder="Enter value"
                        type="number"
                      />
                      <TextInput
                        name="attributes.size.bridge"
                        label="Bridge"
                        placeholder="Enter value"
                        type="number"
                      />
                      <TextInput
                        name="attributes.size.arm_length"
                        label="Arm Length"
                        placeholder="Enter value"
                        type="number"
                      />
                    </div>
                  </>
                ) : null}

                {/* SUNGLASS FIELDS */}
                {values.category === enums.ITEM_CATEGORIES.SUNGLASS ? (
                  <>
                    <div className="input-group">
                      <TextInput
                        name="attributes.shape"
                        label="Shape"
                        placeholder="Enter shape"
                      />
                      <TextInput
                        name="attributes.frame_material"
                        label="Frame Material"
                        placeholder="Enter material"
                      />
                    </div>
                    <div className="input-group">
                      <TextInput
                        name="attributes.frame_color"
                        label="Frame Color"
                        placeholder="Enter color"
                      />
                      <TextInput
                        name="attributes.lens_type"
                        label="Lens Type"
                        placeholder="Enter lens type"
                      />
                    </div>
                    <div className="input-group">
                      <SelectInput
                        name="attributes.gender"
                        label="Gender"
                        values={[
                          { label: "Select Gender", value: "" },
                          ...Object.values(enums.GENDER).map((g) => ({
                            label: g,
                            value: g,
                          })),
                        ]}
                      />
                    </div>
                  </>
                ) : null}

                {/* ACCESSORY FIELDS */}
                {values.category === enums.ITEM_CATEGORIES.ACCESSORIES ? (
                  <>
                    <div className="input-group">
                      <TextInput
                        name="attributes.type"
                        label="Type"
                        placeholder="Enter type"
                      />
                      <TextInput
                        name="attributes.material"
                        label="Material"
                        placeholder="Enter material"
                      />
                      <TextInput
                        name="attributes.color"
                        label="Color"
                        placeholder="Enter color"
                      />
                    </div>
                  </>
                ) : null}
              </div>

              <div className="prices-details">
                <div className="form-section-title">
                  <span>03</span>
                  <h1>Pricing & Warranty</h1>
                </div>
                <div className="input-group">
                  <PriceInput
                    name="purchasePrice"
                    label="Purchase Price"
                    placeholder="Amount"
                  />
                  <PriceInput
                    name="sellingPrice"
                    label="Selling Price"
                    placeholder="Amount"
                  />
                  <TextInput
                    name="warrantyPeriodMonths"
                    label="Warranty (Months)"
                    placeholder="Month count"
                    type="number"
                  />
                </div>
              </div>

              <div className="form-actions">
                <Button type="submit" icon={<DownloadIcon />} text="Submit" />
              </div>
            </Form>
          )}
        </Formik>
      </Section>
      <Loading isLoading={isPending} variant="fullScreen" />
    </div>
  );
}

export default AddItemPage;
